import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Package, ShoppingCart, FileText, MessageSquare, TrendingUp, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    products: 0,
    orders: 0,
    quotes: 0,
    contacts: 0,
    revenue: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [productsRes, ordersRes, quotesRes, contactsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/admin/quotes`),
        axios.get(`${API}/admin/contacts`)
      ]);

      const revenue = ordersRes.data.reduce((sum, order) => sum + order.total, 0);

      setStats({
        products: productsRes.data.length,
        orders: ordersRes.data.length,
        quotes: quotesRes.data.length,
        contacts: contactsRes.data.length,
        revenue
      });

      setRecentOrders(ordersRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const seedData = async () => {
    try {
      await axios.post(`${API}/admin/seed`);
      fetchDashboardData();
    } catch (error) {
      console.error('Error seeding data:', error);
    }
  };

  const statCards = [
    { label: 'Products', value: stats.products, icon: Package, color: 'text-blue-500', bg: 'bg-blue-50' },
    { label: 'Orders', value: stats.orders, icon: ShoppingCart, color: 'text-green-500', bg: 'bg-green-50' },
    { label: 'Quote Requests', value: stats.quotes, icon: FileText, color: 'text-purple-500', bg: 'bg-purple-50' },
    { label: 'Messages', value: stats.contacts, icon: MessageSquare, color: 'text-orange-500', bg: 'bg-orange-50' },
  ];

  return (
    <AdminLayout title="Dashboard">
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : (
        <div className="space-y-8" data-testid="admin-dashboard">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((stat) => (
              <div
                key={stat.label}
                className="bg-white p-6 shadow-sm"
                data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 ${stat.bg} flex items-center justify-center rounded-lg`}>
                    <stat.icon size={24} className={stat.color} />
                  </div>
                  <TrendingUp size={20} className="text-green-500" />
                </div>
                <p className="text-3xl font-bold text-[#1A1A1A]">{stat.value}</p>
                <p className="text-sm text-[#6B7280]">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Revenue Card */}
          <div className="bg-[#1A1A1A] p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Revenue</p>
                <p className="text-4xl font-bold mt-2">${stats.revenue.toFixed(2)}</p>
              </div>
              <div className="w-16 h-16 bg-[#8B5E3C] flex items-center justify-center rounded-lg">
                <DollarSign size={32} />
              </div>
            </div>
          </div>

          {/* Actions */}
          {stats.products === 0 && (
            <div className="bg-[#8B5E3C]/10 border border-[#8B5E3C] p-6">
              <h3 className="font-semibold text-[#1A1A1A] mb-2">Get Started</h3>
              <p className="text-[#6B7280] mb-4">
                No products found. Seed the database with sample products to get started.
              </p>
              <Button onClick={seedData} data-testid="seed-data-btn">
                Seed Sample Data
              </Button>
            </div>
          )}

          {/* Recent Orders */}
          <div className="bg-white p-6 shadow-sm" data-testid="recent-orders">
            <h2 className="font-manrope text-xl font-semibold text-[#1A1A1A] mb-6">
              Recent Orders
            </h2>
            {recentOrders.length === 0 ? (
              <p className="text-[#6B7280] text-center py-8">No orders yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E5E7EB]">
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Order ID</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Customer</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Total</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((order) => (
                      <tr key={order.id} className="border-b border-[#E5E7EB]">
                        <td className="py-3 px-4 font-mono text-sm">{order.id.slice(0, 8)}...</td>
                        <td className="py-3 px-4">{order.customer_name}</td>
                        <td className="py-3 px-4 font-medium">${order.total.toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            order.status === 'completed' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
